import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import authService from "../services/authService";

export default function SignIn() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });
  const [formError, setFormError] = useState(""); // for general/server errors

  const navigate = useNavigate();

  // Regexes for validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const uppercaseRegex = /[A-Z]/;
  const numberRegex = /[0-9]/;
  const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

  // Validate email on blur or change (after touched)
  useEffect(() => {
    if (touched.email) {
      setErrors((prev) => ({
        ...prev,
        email: emailRegex.test(formData.email)
          ? ""
          : "Please enter a valid email address.",
      }));
    }
  }, [formData.email, touched.email]);

  // Validate password live and update criteria checks
  useEffect(() => {
    const pass = formData.password;
    setPasswordChecks({
      length: pass.length >= 6,
      uppercase: uppercaseRegex.test(pass),
      number: numberRegex.test(pass),
      specialChar: specialCharRegex.test(pass),
    });

    if (touched.password) {
      const allValid =
        pass.length >= 6 &&
        uppercaseRegex.test(pass) &&
        numberRegex.test(pass) &&
        specialCharRegex.test(pass);
      setErrors((prev) => ({
        ...prev,
        password: allValid ? "" : "Password does not meet the criteria.",
      }));
    }
  }, [formData.password, touched.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear form error on field edit
    setFormError("");
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  const isFormValid =
    !errors.email &&
    !errors.password &&
    formData.email.length > 0 &&
    formData.password.length > 0 &&
    Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent submit if not valid
    if (!isFormValid) {
      setTouched({ email: true, password: true });
      setFormError("Please fix validation errors.");
      return;
    }

    setFormError(""); // clear existing error

    try {
      // User signin page should ONLY try user login
      const response = await authService.userLogin(formData.email, formData.password);
      if (response.success) {
        navigate("/user/dashboard");
        return;
      }
      // If login didn't succeed but no error was thrown
      setFormError("Invalid email or password");
    } catch (error) {
      // Don't log expected authentication failures to console
      // Invalid credentials is a normal user error, not a system error
      // Only log unexpected errors
      if (error.message && !error.message.includes('Invalid credentials') && !error.message.includes('Login failed')) {
        console.error('Unexpected login error:', error);
      }
      
      // Show user-friendly error message
      if (error.message && (error.message.includes('Invalid credentials') || error.message.includes('Login failed'))) {
        setFormError("Invalid email or password");
      } else {
        setFormError(error.message || "An error occurred. Please try again.");
      }
    }
  };

  return (
    <div className="container py-5" style={{ maxWidth: 440 }}>
      <h2
        className="fw-bold mb-4 text-center"
        style={{ fontFamily: "'Poppins', sans-serif", color: "#6B46C1" }}
      >
        Sign In
      </h2>

      <form noValidate onSubmit={handleSubmit}>
        {/* Global error */}
        {formError && (
          <div className="alert alert-danger" role="alert">
            {formError}
          </div>
        )}

        {/* Email Field */}
        <div className="mb-4">
          <label htmlFor="email" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
            Email <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className={`form-control ${touched.email && errors.email ? "is-invalid" : ""}`}
            placeholder="you@example.com"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            autoComplete="email"
          />
          {touched.email && errors.email && (
            <div className="invalid-feedback">{errors.email}</div>
          )}
        </div>

        {/* Password Field */}
        <div className="mb-4 position-relative">
          <label htmlFor="password" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
            Password <span className="text-danger">*</span>
          </label>
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            className={`form-control ${touched.password && errors.password ? "is-invalid" : ""}`}
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={toggleShowPassword}
            className="btn btn-outline-secondary btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
            style={{ zIndex: 5, borderRadius: '0 0.375rem 0.375rem 0', marginTop: '15px' }}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <i className="bi bi-eye-slash-fill"></i>
            ) : (
              <i className="bi bi-eye-fill"></i>
            )}
          </button>
          {touched.password && errors.password && (
            <div className="invalid-feedback d-block">{errors.password}</div>
          )}
        </div>

        {/* Forgot Password Link */}
        <div className="text-end mb-2">
          <Link
            to="/forgot-password"
            className="text-decoration-none small fw-semibold"
            style={{ color: "#6B46C1" }}
          >
            Forgot Password?
          </Link>
        </div>

        {/* Password requirements box */}
        <div className="mb-4 p-3 border rounded bg-light" style={{ fontSize: 14, userSelect: "none" }}>
          <ul className="mb-0 ps-3">
            <PasswordLine
              isValid={passwordChecks.length}
              text="Minimum 6 characters"
              colorWhenValid="#28a745"
              colorWhenInvalid="#dc3545"
            />
            <PasswordLine
              isValid={passwordChecks.uppercase}
              text="At least 1 uppercase letter"
              colorWhenValid="#28a745"
              colorWhenInvalid="#dc3545"
            />
            <PasswordLine
              isValid={passwordChecks.number}
              text="At least 1 number"
              colorWhenValid="#28a745"
              colorWhenInvalid="#dc3545"
            />
            <PasswordLine
              isValid={passwordChecks.specialChar}
              text="At least 1 special character (!@#$%^&*)"
              colorWhenValid="#28a745"
              colorWhenInvalid="#dc3545"
            />
          </ul>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="btn btn-primary w-100 fw-semibold"
          disabled={!isFormValid}
          aria-disabled={!isFormValid}
        >
          Sign In
        </button>
      </form>
    </div>
  );
}

function PasswordLine({ isValid, text, colorWhenValid, colorWhenInvalid }) {
  return (
    <li style={{ color: isValid ? colorWhenValid : colorWhenInvalid }}>
      <i className={`me-2 bi ${isValid ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
      {text}
    </li>
  );
}
